<template>
  <section class="rebate-banner">
    <div class="banner-top">
      <span class="history-badge"
        >💰 {{ t('promo.rebate.historyTotal') }} <span>{{ fmt(historyTotal) }}</span></span
      >
      <button
        type="button"
        class="help-btn"
        :aria-label="t('promo.rebate.rulesLabel')"
        @click="emit('help')"
      >
        ?
      </button>
    </div>
    <p class="claim-label">{{ t('promo.rebate.claimableLabel') }}</p>
    <p class="claim-amount">{{ fmt(amount) }}</p>
    <button
      v-if="!claimed"
      type="button"
      class="claim-btn"
      :class="{ 'is-loading': loading }"
      :disabled="disabled || loading"
      @click="onClaim"
    >
      {{ t('promo.rebate.claimNow', { amount: fmt(amount) }) }}
    </button>
    <button v-else type="button" class="claim-btn claimed" disabled>
      {{ t('promo.rebate.claimed') }}
    </button>
    <!-- batch 模式领取后等待派发：按服务端给的绝对时刻倒计时，到点重新拉面板 -->
    <i18n-t
      v-if="showCountdown"
      keypath="promo.rebate.nextPayout"
      tag="div"
      class="next-payout"
      scope="global"
    >
      <template #countdown>
        <CountdownText
          class="countdown"
          :target="settle?.next_settle_at"
          @expire="emit('refresh')"
        />
      </template>
    </i18n-t>
    <div v-else-if="settle?.static_text" class="next-payout">{{ settle.static_text }}</div>
  </section>
</template>

<script setup lang="ts">
import type { RakebackSettleInfoDto } from '~/types/api/promo'
import { formatBaseAmt } from '~/utils/transfer/money'
import { parseInstant } from '~/utils/time'

const { t } = useI18n()

const props = defineProps<{
  historyTotal: number
  amount: number
  claimed: boolean
  /** 领取请求在途：禁用按钮防重复点击 */
  loading?: boolean
  /** 派发状态（面板 / 领取结果的 settle）：show_countdown + next_settle_at 或 static_text */
  settle?: RakebackSettleInfoDto | null
  disabled?: boolean
}>()
/** refresh：派发时刻到点，由父级重新拉返水面板（不在本地改状态） */
const emit = defineEmits<{ claim: []; help: []; refresh: [] }>()

const userStore = useUserStore()
const fmt = (n: number) => formatBaseAmt(n, userStore.baseCurrency)

const showCountdown = computed(
  () => !!props.settle?.show_countdown && parseInstant(props.settle.next_settle_at) !== null,
)

const onClaim = () => {
  if (props.amount <= 0) return
  emit('claim')
}
</script>

<style scoped lang="scss">
.rebate-banner {
  margin: 12px 14px 8px;
  padding: 20px 16px;
  border-radius: 18px;
  text-align: center;
  background: linear-gradient(135deg, #7a3aff, #b13aff);
  box-shadow: var(--shadow);
}

.banner-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.history-badge {
  display: inline-block;
  font-size: 11px;
  background: rgba(0, 0, 0, 0.22);
  padding: 4px 10px;
  border-radius: var(--r-pill);
  color: #fff;
}

.help-btn {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.25);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  display: grid;
  place-items: center;
}

.claim-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  margin-top: 12px;
}

.claim-amount {
  font-size: 34px;
  font-weight: 900;
  color: #fff;
  margin: 2px 0 12px;
}

.claim-btn {
  width: 100%;
  padding: 13px;
  border-radius: 14px;
  background: #fff;
  color: #7a3aff;
  font-weight: 800;
  font-size: 15px;
}

.claim-btn:disabled {
  color: var(--muted);
  cursor: default;
}

.claim-btn.is-loading:disabled {
  color: inherit;
  opacity: 0.65;
}

.claim-btn.claimed {
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
  cursor: default;
}

.next-payout {
  margin-top: 9px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
}

.next-payout .countdown {
  font-style: normal;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.6px;
  font-weight: 900;
  font-size: 13px;
  color: #fff;
  background: linear-gradient(135deg, #ff5a3c, #ff2d55);
  padding: 2px 8px;
  border-radius: 7px;
  box-shadow: 0 2px 8px rgba(255, 45, 85, 0.4);
  animation: cdpulse 1.4s ease-in-out infinite;
}

.next-payout b {
  font-weight: 900;
  color: #ffe08a;
}
</style>
